/**
 * Life Blueprint V2.9.9 — Migration Batch 03 (Sheet27: B45 / B46 / B47)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * STRICT SCOPE
 *   This file implements ONLY the three audited Sheet27 cells B45, B46, B47.
 *   No B48+, no downstream Sheet28/29/30, no upstream generation of B42/B44.
 *
 * GOLDEN MASTER PROVENANCE (audited)
 *   Sheet27!B45  =IF( '01_个人资料'!$C$32 = <manual-mode-token>,
 *                    IF('01_个人资料'!$I$19<>"", LEFT('01_个人资料'!$I$19,1), ""),
 *                    VLOOKUP(B44, <Sheet40>!$A:$D, 2, FALSE) )
 *   Sheet27!B46  =IF( '01_个人资料'!$C$32 = <manual-mode-token>,
 *                    IF('01_个人资料'!$I$19<>"", RIGHT('01_个人资料'!$I$19,1), ""),
 *                    VLOOKUP(B42, <Sheet41>!$A:$E, 2, FALSE) )
 *   Sheet27!B47  = B45 & B46
 *
 *   Lookup sources (independently resolved by Excel itself):
 *     B45 → Worksheet Index 40, CodeName "Sheet40"
 *     B46 → Worksheet Index 41, CodeName "Sheet41"
 *
 *   Direct-branch evidence for the current Golden Master sample:
 *     '01_个人资料'!$C$32 = <manual-mode-token>  →  FALSE
 *     (i.e., the automatic VLOOKUP branch fires. Manual override is NOT active.)
 *     I19 <> "" was directly TRUE, but does not affect this sample because
 *     the outer manual-branch guard is FALSE.
 *
 *   Audited automatic-branch lookup evidence (this sample):
 *     B44 = 9  →  Sheet40!A10 = 9, Sheet40!B10 = U+58EC (壬)   →  B45 = 壬
 *     B42 = 3  →  Sheet41!A4  = 3, Sheet41!B4  = U+5BC5 (寅)   →  B46 = 寅
 *     B47 = B45 & B46 = 壬寅
 *
 * EVIDENCE-ORIENTED INPUT CONTRACT (Batch 03 only — NOT final API)
 *   {
 *     manualOverrideBranchActive : boolean         // audited branch decision
 *     upstreamValue_B44          : integer         // required in automatic branch
 *     upstreamValue_B42          : integer         // required in automatic branch
 *     upstreamI19                : string|null     // audited '01_个人资料'!$I$19
 *     reference                  : { Sheet40, Sheet41 }  // partial GM reference
 *   }
 *
 * WHY `manualOverrideBranchActive` INSTEAD OF THE ACTUAL COMPARISON
 *   The manual-mode token ('01_个人资料'!$C$32 = <manual-mode-token>) has
 *   NOT been supplied as machine-readable Golden Master evidence. Any
 *   reconstruction of that comparison here would be an invented rule.
 *   The caller supplies the branch decision that Excel itself evaluated;
 *   the port preserves the audited two-branch structure without inventing
 *   the hidden Unicode token.
 *
 * BUSINESS MEANING
 *   The business meaning of B45, B46, B47 is intentionally NOT labelled.
 *   The lookup tables Sheet40 / Sheet41 are treated as opaque reference
 *   evidence — no metaphysics/domain labels attached.
 *
 * DETERMINISM
 *   No randomness. No wall clock. Given identical input this module returns
 *   byte-wise identical output on every run.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * Partial reference-data evidence for the current Golden Master sample.
 *
 * This is INTENTIONALLY incomplete. Only the two audited rows required by
 * the current sample have been captured. The `status` sentinel
 * `PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE` MUST be preserved on any
 * consumer that surfaces this data so nobody treats it as a full ReferenceData
 * table. The M02 manifest entry MUST NOT be flipped to IMPLEMENTED merely
 * because two rows have been migrated here.
 */
export const PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE = Object.freeze({
  Sheet40: Object.freeze({
    codeName: "Sheet40",
    worksheetIndex: 40,
    status: "PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE",
    note:
      "Only the rows required by currently audited Golden Master samples " +
      "have been migrated. This is NOT a complete Sheet40 reference table. " +
      "M02 must remain NOT_STARTED.",
    // Two Excel columns; the port uses `a` (lookup key) and `b` (return value).
    // Rows are additive across batches — never rewrite an existing row.
    rows: Object.freeze([
      Object.freeze({
        a: 9,
        b: "\u58EC", // 壬
        unicodeCodePoint: 22764,
        sourceCell: "Sheet40!A10:B10",
        auditedInBatch: "Batch03",
      }),
      Object.freeze({
        a: 3,
        b: "\u4E19", // 丙
        unicodeCodePoint: 19993,
        sourceCell: "Sheet40!A4:B4",
        auditedInBatch: "Batch05",
      }),
    ]),
  }),
  Sheet41: Object.freeze({
    codeName: "Sheet41",
    worksheetIndex: 41,
    status: "PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE",
    note:
      "Only the rows required by currently audited Golden Master samples " +
      "have been migrated. This is NOT a complete Sheet41 reference table. " +
      "M02 must remain NOT_STARTED.",
    rows: Object.freeze([
      Object.freeze({
        a: 3,
        b: "\u5BC5", // 寅
        unicodeCodePoint: 23493,
        sourceCell: "Sheet41!A4:B4",
        auditedInBatch: "Batch03",
      }),
      Object.freeze({
        a: 9,
        b: "\u7533", // 申
        unicodeCodePoint: 30003,
        sourceCell: "Sheet41!A10:B10",
        auditedInBatch: "Batch05",
      }),
    ]),
  }),
});

/**
 * Excel-compatible exact-match VLOOKUP over a partial reference table.
 * If the key is not present in the currently migrated audited rows this
 * throws with a machine-readable error code — never returns a guessed value.
 */
function vlookupExactMatch(key, refTable) {
  if (!refTable || !Array.isArray(refTable.rows)) {
    throw new TypeError("[Batch03·VLOOKUP] reference table missing or malformed");
  }
  const row = refTable.rows.find((r) => r.a === key);
  if (!row) {
    const complete = refTable.status === "AUTHORITATIVE_DATASET_COMPLETE";

    const err = new Error(
      complete
        ? `[Batch03·VLOOKUP] Missing ${refTable.codeName} key ${JSON.stringify(key)} ` +
          `in AUTHORITATIVE_DATASET_COMPLETE. No result guessed.`
        : `[Batch03·VLOOKUP] Missing ${refTable.codeName} key ${JSON.stringify(key)}. ` +
          `Only PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE has been migrated for ` +
          `this reference table; the required row has not yet been audited. ` +
          `No result guessed.`
    );

    err.code = complete
      ? "MISSING_REFERENCE_KEY"
      : "MISSING_REFERENCE_EVIDENCE";
    err.refTable = refTable.codeName;
    err.key = key;
    throw err;
  }
  return row.b;
}

function isI19NonEmpty(v) {
  // Excel: I19<>"" is TRUE for any non-empty string. Null / undefined / ""
  // all fail the guard.
  return typeof v === "string" && v.length > 0;
}

/**
 * Audited manual-mode comparison token for '01_个人资料'!$C$32.
 *
 * Directly extracted from the Excel Golden Master via VBA AscW:
 *   TOKEN LEN = 4
 *   25163  21160  -30330  30422
 * Decoded Unicode code points (VBA returns signed Int16):
 *   0x624B (25163) = 手
 *   0x52A8 (21160) = 动
 *   0x8986 (35206, i.e. -30330 + 65536) = 覆
 *   0x76D6 (30422) = 盖
 * → "手动覆盖"
 *
 * The current audited '01_个人资料'!C32 value for the current Golden Master
 * sample was independently extracted as "自动排盘" (AscW: -32278 21160 25490
 * 30424 → 0x81EA 0x52A8 0x6392 0x76D8). Excel directly evaluated
 * C32 = <manual-mode token> → FALSE, so the automatic VLOOKUP branch fires.
 *
 * NO OTHER C32 VALUES ARE INFERRED. "自动排盘" is audited only as the current
 * Golden Master value — it is NOT declared here as a canonical "automatic
 * mode" enum. Any C32 string that is not === MANUAL_MODE_TOKEN takes the
 * automatic branch, per the audited Excel semantics.
 */
export const MANUAL_MODE_TOKEN = "\u624B\u52A8\u8986\u76D6"; // 手动覆盖

/**
 * Derive the manual-mode branch decision for the current slice.
 *
 * Two supported input styles:
 *   1. `upstreamC32: string` — the audited '01_个人资料'!$C$32 value. The
 *      port evaluates `upstreamC32 === MANUAL_MODE_TOKEN` directly, matching
 *      the audited Excel formula.
 *   2. `manualOverrideBranchActive: boolean` — legacy explicit branch input
 *      preserved so existing IMPLEMENTATION_MECHANIC_TEST cases (which
 *      predate the token audit) continue to exercise the branch mechanics
 *      without needing to reproduce the Unicode token.
 *
 * If both are supplied, `upstreamC32` wins (audited evidence beats caller
 * intent). If neither is supplied this throws.
 */
export function resolveManualBranchActive(slice) {
  if (typeof slice.upstreamC32 === "string") {
    return slice.upstreamC32 === MANUAL_MODE_TOKEN;
  }
  if (typeof slice.manualOverrideBranchActive === "boolean") {
    return slice.manualOverrideBranchActive;
  }
  throw new TypeError(
    "[Batch03] slice must supply either upstreamC32 (string) or " +
      "manualOverrideBranchActive (boolean)"
  );
}

/**
 * Sheet27!B45.
 * @param {{
 *   manualOverrideBranchActive: boolean,
 *   upstreamValue_B44?: number,
 *   upstreamI19?: string|null,
 *   reference: { Sheet40: Object }
 * }} slice
 * @returns {string}
 */
export function computeB45(slice) {
  if (slice === null || typeof slice !== "object") {
    throw new TypeError("[Batch03·B45] slice must be an object");
  }
  const { upstreamValue_B44, upstreamI19, reference } = slice;
  const manualActive = resolveManualBranchActive(slice);
  if (manualActive === true) {
    if (isI19NonEmpty(upstreamI19)) {
      return upstreamI19.substring(0, 1); // LEFT(I19, 1)
    }
    return "";
  }
  // Automatic branch — VLOOKUP(B44, Sheet40, 2, FALSE)
  if (!Number.isInteger(upstreamValue_B44)) {
    throw new TypeError(
      "[Batch03·B45] upstreamValue_B44 must be an integer in the automatic branch"
    );
  }
  if (!reference || !reference.Sheet40) {
    throw new TypeError("[Batch03·B45] reference.Sheet40 required in the automatic branch");
  }
  return vlookupExactMatch(upstreamValue_B44, reference.Sheet40);
}

/**
 * Sheet27!B46.
 * @param {{
 *   manualOverrideBranchActive: boolean,
 *   upstreamValue_B42?: number,
 *   upstreamI19?: string|null,
 *   reference: { Sheet41: Object }
 * }} slice
 * @returns {string}
 */
export function computeB46(slice) {
  if (slice === null || typeof slice !== "object") {
    throw new TypeError("[Batch03·B46] slice must be an object");
  }
  const { upstreamValue_B42, upstreamI19, reference } = slice;
  const manualActive = resolveManualBranchActive(slice);
  if (manualActive === true) {
    if (isI19NonEmpty(upstreamI19)) {
      return upstreamI19.substring(upstreamI19.length - 1); // RIGHT(I19, 1)
    }
    return "";
  }
  // Automatic branch — VLOOKUP(B42, Sheet41, 2, FALSE)
  if (!Number.isInteger(upstreamValue_B42)) {
    throw new TypeError(
      "[Batch03·B46] upstreamValue_B42 must be an integer in the automatic branch"
    );
  }
  if (!reference || !reference.Sheet41) {
    throw new TypeError("[Batch03·B46] reference.Sheet41 required in the automatic branch");
  }
  return vlookupExactMatch(upstreamValue_B42, reference.Sheet41);
}

/**
 * Sheet27!B47  = B45 & B46
 * Excel `&` is a string concatenation of the two operands (already strings
 * in this branch — VLOOKUP return column is text, LEFT/RIGHT are text).
 */
export function computeB47(B45, B46) {
  return String(B45) + String(B46);
}

/**
 * Aggregate entry point for Batch 03. Consumes both branches and returns
 * B45, B46, B47 plus provenance. Callers may omit `reference` and pass their
 * own audited partial evidence; if omitted the exported
 * PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE is used.
 *
 * @param {Object} slice
 * @returns {{B45:string, B46:string, B47:string, _provenance: Object}}
 */
export function computeBatch03Sheet27(slice) {
  if (slice === null || typeof slice !== "object") {
    throw new TypeError("[Batch03] slice must be an object");
  }
  const reference = slice.reference || PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE;
  const sliceWithRef = { ...slice, reference };
  const B45 = computeB45(sliceWithRef);
  const B46 = computeB46(sliceWithRef);
  const B47 = computeB47(B45, B46);
  return {
    B45,
    B46,
    B47,
    _provenance: {
      excelSources: ["Sheet27!B45", "Sheet27!B46", "Sheet27!B47"],
      formulas: {
        "Sheet27!B45":
          "=IF('01_个人资料'!$C$32=<manual-mode-token>," +
          "IF('01_个人资料'!$I$19<>\"\",LEFT('01_个人资料'!$I$19,1),\"\")," +
          "VLOOKUP(B44,<Sheet40>!$A:$D,2,FALSE))",
        "Sheet27!B46":
          "=IF('01_个人资料'!$C$32=<manual-mode-token>," +
          "IF('01_个人资料'!$I$19<>\"\",RIGHT('01_个人资料'!$I$19,1),\"\")," +
          "VLOOKUP(B42,<Sheet41>!$A:$E,2,FALSE))",
        "Sheet27!B47": "=B45&B46",
      },
      lookupSources: {
        B45: { worksheetIndex: 40, codeName: "Sheet40" },
        B46: { worksheetIndex: 41, codeName: "Sheet41" },
      },
      auditedRecords: {
        "Sheet40!A10:B10": { key: 9, value: "\u58EC", unicodeCodePoint: 22764 },
        "Sheet41!A4:B4":  { key: 3, value: "\u5BC5", unicodeCodePoint: 23493 },
      },
      currentGoldenMasterBranch: {
        firstLevelManualComparison: false,
        manualModeToken: MANUAL_MODE_TOKEN,
        currentC32AuditedValue: "\u81EA\u52A8\u6392\u76D8", // 自动排盘 (audited current GM)
        note:
          "Manual-mode token '手动覆盖' (U+624B U+52A8 U+8986 U+76D6) has now " +
          "been directly extracted from Excel via VBA AscW. The current " +
          "audited '01_个人资料'!$C$32 value is '自动排盘' (U+81EA U+52A8 " +
          "U+6392 U+76D8). Excel directly evaluated (C32 = manual-mode " +
          "token) → FALSE, so the automatic VLOOKUP branch fires. No other " +
          "C32 values are inferred; '自动排盘' is audited only as the current " +
          "Golden Master value, not declared as a canonical enum.",
      },
      partialReferenceEvidence: {
        Sheet40: reference.Sheet40 && reference.Sheet40.status,
        Sheet41: reference.Sheet41 && reference.Sheet41.status,
      },
      note:
        "Business meaning of B45/B46/B47 and of Sheet40/Sheet41 rows is " +
        "intentionally NOT labelled; no independent evidence has established " +
        "their semantics.",
    },
  };
}
